import React from "react";
import { motion } from "framer-motion";
import { Activity, ArrowRight, Bell, BookOpen, Smartphone, Sparkles, TrendingUp } from "lucide-react";
import { Reveal } from "./Shared";
import { productUrl } from "@/config/siteUrls";

const CompanionDownload = () => (
  <section
    id="adaptive-companion"
    data-testid="adaptive-companion-download"
    className="relative overflow-hidden border-t py-24 forr-canvas-alt sm:py-32"
    style={{ borderColor: "var(--forr-line-soft)" }}
  >
    <div className="absolute -top-40 right-[-160px] h-[720px] w-[720px] forr-radial-teal opacity-70 pointer-events-none" />
    <div className="relative mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-16">
      <Reveal>
        <div className="rounded-[32px] p-7 forr-surface sm:p-10 lg:p-14" style={{ border: "1px solid var(--forr-line)", boxShadow: "var(--forr-shadow-lg)" }}>
          <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
            <div>
              <div className="inline-flex items-center gap-3">
                <span className="forr-hairline" />
                <span className="forr-overline">APOS Companion · Coming soon</span>
              </div>
              <h2 className="mt-6 font-display text-4xl font-medium leading-[1.04] tracking-[-0.03em] forr-text-ink sm:text-5xl lg:text-6xl">
                Turn your assessment into daily nervous-system guidance.
              </h2>
              <p className="mt-6 max-w-2xl text-lg font-light leading-relaxed forr-text-soft sm:text-xl">
                APOS Companion will connect your saved Adaptive Profile to current-state check-ins,
                matched physiological actions, planning, progress, learning, and Companion AI.
              </p>
              <p className="mt-5 max-w-2xl text-sm font-light leading-relaxed forr-text-mute sm:text-base">
                The Companion app is in beta while subscription and payment access are prepared.
                The Adaptive Assessment and personalized results are available now.
              </p>
              <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <a href={productUrl("/app#early-access")} className="inline-flex items-center gap-3 rounded-full px-7 py-4 text-sm font-semibold tracking-wide forr-btn-primary">
                    <Bell size={17} /> Save my place
                  </a>
                </motion.div>
                <a href={productUrl("/assessment/start")} className="inline-flex items-center gap-2 rounded-full px-6 py-4 text-sm font-medium forr-btn-secondary">
                  Take the assessment <ArrowRight size={16} />
                </a>
              </div>
              <p className="mt-5 text-xs forr-text-mute">
                Complete the assessment now and your saved results will be ready to connect with Companion when access opens.
              </p>
            </div>

            <div className="grid gap-3">
              {[
                { icon: Smartphone, title: "Profile-first app home", body: "Your saved assessment profile, current nervous-system state, priorities, and next steps in one place." },
                { icon: Activity, title: "State-matched actions", body: "Current-state check-ins followed by breathing, movement, recovery, and preparation options matched to you." },
                { icon: TrendingUp, title: "Progress and response history", body: "See how your responses change across repeated check-ins, actions, sleep, recovery, and daily demand." },
                { icon: BookOpen, title: "Methodology inside the app", body: "Learn how nervous-system state, exercise physiology, recovery, environment, and behaviour connect." },
                { icon: Sparkles, title: "Adaptive Companion AI", body: "Understand your results and explore practical alternatives when the first option does not fit your day." },
              ].map(({ icon: Icon, title, body }) => (
                <div key={title} className="flex items-start gap-4 rounded-[20px] p-5 forr-panel-information">
                  <div className="rounded-xl bg-white p-2.5"><Icon size={20} className="forr-text-teal-deep" /></div>
                  <div>
                    <h3 className="font-display text-lg forr-text-ink">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed forr-text-soft">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default CompanionDownload;
